import * as THREE from 'three'
import { extractGltfLights, mapGltfLightsById } from './h3dLights.js'

const FLOATS_PER_VERTEX = 3
// The types the H3D0 runtime can render. Anything else (directional, for now) is
// reported as skipped rather than silently dropped.
const EXPORTED_LIGHT_TYPES = new Set(['point', 'spot'])
// glTF defines a spot's aim as the local -Z axis of its node.
const SPOT_LOCAL_AXIS = Object.freeze([0, 0, -1])

export function createH3dPrototypeDataFromGltf(gltf) {
  gltf.scene.updateWorldMatrix(true, true)

  const positions = []
  const normals = []
  const indices = []
  const primitives = []
  const materials = []
  const materialMap = new Map()

  const report = {
    sourceMeshes: 0,
    sourcePrimitives: 0,
    sourceTriangles: 0,
    exportedPrimitives: 0,
    exportedTriangles: 0,
    meshes: [],
    skipped: [],
    warnings: [],
  }

  gltf.scene.traverse(node => {
    if (!node.isMesh || !node.geometry?.isBufferGeometry) return
    report.sourceMeshes += 1

    const path = nodePath(node)
    const geometry = node.geometry
    const determinant = node.matrixWorld.determinant()
    const mirrored = determinant < 0
    const record = {
      path,
      visible: node.visible,
      vertexCount: geometry.getAttribute('position')?.count ?? 0,
      determinant,
      mirrored,
      exported: false,
      reason: null,
      triangleCount: 0,
      worldBounds: null,
    }
    report.meshes.push(record)

    if (!node.visible) {
      record.reason = 'node.visible is false'
      return
    }

    const position = geometry.getAttribute('position')
    const normal = geometry.getAttribute('normal')
    if (!position) {
      record.reason = 'missing POSITION attribute'
      report.warnings.push(`${path}: ${record.reason}; skipped`)
      return
    }
    if (!normal) {
      record.reason = 'missing NORMAL attribute'
      report.warnings.push(`${path}: ${record.reason}; skipped`)
      return
    }

    // Mirror Three.js's own rendering rules exactly: it draws the intersection of
    // drawRange and each geometry group, and nothing outside either. Anything this
    // compiler treats as "renderable" must line up with that or the coverage check
    // below (sourceTriangles === exportedTriangles) would be comparing apples to oranges.
    const sourceIndex = geometry.getIndex()
    const fullCount = sourceIndex ? sourceIndex.count : position.count
    const drawRange = geometry.drawRange || { start: 0, count: Infinity }
    const rangeStart = Math.max(0, drawRange.start)
    const rangeEnd = Math.min(fullCount, drawRange.start + drawRange.count)
    if (rangeEnd <= rangeStart) {
      record.reason = 'drawRange excludes all geometry'
      report.warnings.push(`${path}: ${record.reason}`)
      return
    }
    if ((rangeEnd - rangeStart) % 3 !== 0) {
      record.reason = `drawRange [${rangeStart}, ${rangeEnd}) is not a whole number of triangles`
      report.warnings.push(`${path}: ${record.reason}; skipped`)
      return
    }
    if (rangeStart > 0 || rangeEnd < fullCount) {
      report.warnings.push(`${path}: drawRange limits geometry to [${rangeStart}, ${rangeEnd}) of ${fullCount}`)
    }
    if (!sourceIndex) {
      report.warnings.push(`${path}: geometry.index is null; generated a sequential index buffer`)
    }

    const groups = resolveExportGroups(geometry.groups, rangeStart, rangeEnd, path, report)
    if (!groups.length) {
      record.reason = 'no renderable index range after applying drawRange/groups'
      report.warnings.push(`${path}: ${record.reason}`)
      return
    }

    const vertexOffset = positions.length / FLOATS_PER_VERTEX
    appendTransformedVertices(positions, normals, position, normal, node.matrixWorld)

    const indexOffset = indices.length
    for (let i = rangeStart; i < rangeEnd; i += 1) {
      const vertexIndex = sourceIndex ? sourceIndex.getX(i) : i
      indices.push(vertexIndex + vertexOffset)
    }
    if (mirrored) {
      // A negative matrixWorld determinant flips triangle winding once baked into
      // world space. Reverse it here so front-face culling still matches the
      // mirrored geometry instead of silently disappearing behind back-face culling.
      reverseTriangleWinding(indices, indexOffset, rangeEnd - rangeStart)
      report.warnings.push(`${path}: matrixWorld determinant ${determinant.toFixed(6)} < 0 (mirrored transform); corrected triangle winding`)
    }

    const meshMaterials = Array.isArray(node.material) ? node.material : [node.material]
    let meshTriangles = 0
    for (const group of groups) {
      const material = meshMaterials[group.materialIndex] || meshMaterials[0]
      const count = group.end - group.start
      primitives.push({
        firstIndex: indexOffset + (group.start - rangeStart),
        indexCount: count,
        materialIndex: materialIndexFor(material, materialMap, materials),
      })
      meshTriangles += count / 3
      report.exportedPrimitives += 1
    }

    record.exported = true
    record.triangleCount = meshTriangles
    record.worldBounds = computeWorldBounds(positions, vertexOffset, position.count)
    report.sourceTriangles += meshTriangles
    report.exportedTriangles += meshTriangles
  })

  report.skipped = report.meshes
    .filter(mesh => !mesh.exported)
    .map(mesh => ({ path: mesh.path, reason: mesh.reason }))

  if (!primitives.length) {
    throw new Error('No renderable GLB primitives found')
  }

  if (report.sourceTriangles !== report.exportedTriangles) {
    throw new Error(
      `H3D0 prototype compiler geometry coverage mismatch: ${report.sourceTriangles} renderable source triangles `
      + `vs ${report.exportedTriangles} exported triangles`,
    )
  }

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    indices: new Uint32Array(indices),
    primitives,
    materials,
    lights: collectLights(gltf, report),
    report,
  }
}

/**
 * Collects the punctual lights to bake into H3D0.
 *
 * Ids come from the shared extraction the action editor and preview already use, so a
 * light addressed by an action resolves to the same entry in the compiled file. Colour,
 * intensity and cone angles are taken from the glTF document rather than the loaded
 * THREE lights: those are the values as authored, unaffected by any loader-side unit
 * conversion. Positions are world-space, matching the world transforms baked into the
 * vertices.
 */
function collectLights(gltf, report) {
  const declared = extractGltfLights(gltf.parser?.json)
  if (!declared.length) return []

  const objects = mapGltfLightsById(gltf)
  const worldPosition = new THREE.Vector3()
  const worldDirection = new THREE.Vector3()
  const lights = []

  for (const light of declared) {
    if (!EXPORTED_LIGHT_TYPES.has(light.type)) {
      report.skipped.push(`light ${light.id}: ${light.type} lights are not supported yet`)
      continue
    }
    const object = objects.get(light.id)?.light
    if (!object) {
      report.warnings.push(`light ${light.id}: no matching light in the loaded scene; skipped`)
      continue
    }

    object.getWorldPosition(worldPosition)
    const record = {
      id: light.id,
      type: light.type,
      position: [worldPosition.x, worldPosition.y, worldPosition.z],
      color: [light.color[0], light.color[1], light.color[2]],
      intensity: light.intensity,
    }

    if (light.type === 'spot') {
      // A direction, not a position: the full world transform applies, but only its
      // rotation/scale part - `transformDirection` drops the translation and
      // renormalizes, so nested parents and non-uniform scale both come out right.
      worldDirection.set(...SPOT_LOCAL_AXIS).transformDirection(object.matrixWorld)
      if (worldDirection.lengthSq() === 0 || !Number.isFinite(worldDirection.lengthSq())) {
        report.warnings.push(`light ${light.id}: spot has no usable cone axis; skipped`)
        continue
      }
      record.direction = [worldDirection.x, worldDirection.y, worldDirection.z]
      record.innerConeAngle = light.spot.innerConeAngle
      record.outerConeAngle = light.spot.outerConeAngle
    }

    lights.push(record)
  }
  return lights
}

// Clips geometry.groups (or a single synthetic full-range group when none are
// defined) to the drawRange, exactly like Three.js's own renderBufferDirect does.
// Gaps between groups are legitimate in that model - Three.js does not render them
// either - so they are excluded from the export and called out as a warning rather
// than treated as lost geometry.
function resolveExportGroups(sourceGroups, rangeStart, rangeEnd, path, report) {
  const hasExplicitGroups = Boolean(sourceGroups?.length)
  const rawGroups = hasExplicitGroups
    ? sourceGroups
    : [{ start: rangeStart, count: rangeEnd - rangeStart, materialIndex: 0 }]
  report.sourcePrimitives += rawGroups.length

  const clipped = []
  for (const group of rawGroups) {
    const start = Math.max(rangeStart, group.start)
    const end = Math.min(rangeEnd, group.start + group.count)
    if (end <= start) {
      if (hasExplicitGroups) {
        report.warnings.push(`${path}: geometry group [${group.start}, ${group.start + group.count}) is outside the drawRange; not exported`)
      }
      continue
    }
    clipped.push({ start, end, materialIndex: group.materialIndex ?? 0 })
  }

  if (hasExplicitGroups) {
    const covered = clipped.reduce((sum, g) => sum + (g.end - g.start), 0)
    const total = rangeEnd - rangeStart
    if (covered < total) {
      report.warnings.push(
        `${path}: geometry.groups leave ${total - covered} of ${total} indices uncovered; `
        + 'Three.js does not render those triangles either, so they are excluded from the export',
      )
    }
  }

  return clipped
}

function appendTransformedVertices(targetPositions, targetNormals, position, normal, matrixWorld) {
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrixWorld)
  const p = new THREE.Vector3()
  const n = new THREE.Vector3()

  for (let i = 0; i < position.count; i += 1) {
    p.fromBufferAttribute(position, i).applyMatrix4(matrixWorld)
    n.fromBufferAttribute(normal, i).applyMatrix3(normalMatrix).normalize()
    targetPositions.push(p.x, p.y, p.z)
    targetNormals.push(n.x, n.y, n.z)
  }
}

function reverseTriangleWinding(indices, startOffset, count) {
  for (let i = startOffset; i < startOffset + count; i += 3) {
    const b = indices[i + 1]
    indices[i + 1] = indices[i + 2]
    indices[i + 2] = b
  }
}

function computeWorldBounds(positions, vertexOffset, vertexCount) {
  const box = new THREE.Box3()
  const v = new THREE.Vector3()
  const start = vertexOffset * FLOATS_PER_VERTEX
  for (let i = 0; i < vertexCount; i += 1) {
    const o = start + i * FLOATS_PER_VERTEX
    v.set(positions[o], positions[o + 1], positions[o + 2])
    box.expandByPoint(v)
  }
  return { min: box.min.toArray(), max: box.max.toArray() }
}

function nodePath(node) {
  const parts = []
  let current = node
  while (current) {
    parts.unshift(current.name || current.type || '(unnamed)')
    current = current.parent
  }
  return parts.join('/')
}

function materialIndexFor(material, materialMap, materials) {
  if (materialMap.has(material)) return materialMap.get(material)

  const materialIndex = materials.length
  materialMap.set(material, materialIndex)
  materials.push({
    baseColor: new Float32Array([
      material?.color?.r ?? 1,
      material?.color?.g ?? 1,
      material?.color?.b ?? 1,
      material?.opacity ?? 1,
    ]),
  })
  return materialIndex
}
